import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, SxProps, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionProps } from '@mui/material/Accordion/Accordion';

interface AccordionSectionProps extends Omit<AccordionProps, 'title' | 'children'> {
  defaultExpanded?: boolean;
  title: React.ReactNode;
  actions?: React.ReactNode;
  onViewDetails?: () => void;
  children?: React.ReactNode;
  sxDetails?: SxProps;
  lazyLoad?: boolean;
}

const AccordionSection: React.FC<AccordionSectionProps> = React.memo(
  ({ title, actions, defaultExpanded = true, onViewDetails, children, sxDetails, lazyLoad = false, ...props }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [hasBeenExpanded, setHasBeenExpanded] = useState(defaultExpanded);

    const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded);
      if (isExpanded && !hasBeenExpanded) {
        setHasBeenExpanded(true);
      }
    };

    const handleSummaryClick = (event: React.MouseEvent) => {
      if (!(event.target instanceof HTMLElement && event.target.closest('.MuiAccordionSummary-expandIconWrapper'))) {
        event.stopPropagation();
      }
    };

    return (
      <Accordion
        expanded={expanded}
        onChange={handleChange}
        disableGutters
        sx={{
          '&::before': { display: 'none' },
        }}
        {...props}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={handleSummaryClick}
          sx={{
            flexDirection: 'row-reverse',
            pr: 1,
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginLeft: -3,
              marginRight: 1,
            },
            '& .MuiAccordionSummary-content': {
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              margin: 0,
              borderBottom: '1px solid',
              borderColor: 'border.default',
              paddingBottom: 1,
              '&.Mui-expanded': { margin: 0 },
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h4" color="primary">
              {title}
            </Typography>
            {actions}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, ...sxDetails }}>
          {lazyLoad ? hasBeenExpanded && children : children}
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default AccordionSection;
